/* eslint-disable camelcase */
import {
  CalendarOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleTwoTone,
  FileTextOutlined,
  EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { useState, useEffect, useCallback } from 'react';

import {
  Button,
  Space,
  Skeleton,
  message,
  Typography,
  Divider,
  Dropdown,
  Alert,
  Modal,
  Input,
  Row,
  Col,
  Upload,
  DatePicker,
  Select,
  InputNumber,
  Form,
  Tag,
  Radio
} from 'antd';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import apiConfig from '../../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../../API_Utils/APIUtil/CallApi';
import { PATH } from '../../../API_Utils/constants';
import {
  getError,
  getFormattedAmount,
  getHistoryClient,
  getDateFromISO,
  getFileExtension,
  getMDMTransportClient,
  handleS3Upload,
  showErrorMessage,
  getLatestLrNumber
} from '../../../clientUtils';
import { CommonModalFooter, ModalWrapper } from '../../../Components/Common';
import Address from '../../../Components/Common/Address';
import CancelModal from '../../../Components/Common/CancelModal';
import CustomHeader from '../../../Components/Common/CustomHeader';
import CustomView from '../../../Components/Common/CustomView';
import DeliveryDetailsModal from '../../../Components/Common/DeliveryDetailsModal';
import DocumentCard from '../../../Components/Common/DocumentCard';
import EWayBillDetailsModal from '../../../Components/Common/EWayBillDetailsModal';
import InfoBanner from '../../../Components/Common/InfoBanner';
import AppLayout from '../../../Components/Common/Layout';
import UpdateLrModal from '../../../Components/Common/UpdateLrModal';
import LmDateModal from '../../../Components/Sales/DomesticSalesOrder/LmDateModal';
import * as Constants from '../../../constants';
import {
  getActiveRoEwayBill,
  isRoDeliveryLogisticsMandatory,
  isRoDispatchVehicleMandatory,
  isRoLogisticsScope,
  validateRoLogisticsAgainstEwayBill
} from '../../../Utils/roLogistics';
import { DISPATCH_MODE_OPTIONS, isSelfDispatchMode, sortTransportersByName, wasDispatchedAsSelf } from '../../../Utils/soLogistics';

const { Text, Title } = Typography;
const { confirm } = Modal;
const { TextArea } = Input;
const buttonStyle = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit' };

const STN_ACTION_TYPES = {
  REVERSE_STN: 'REVERSE_STN',
  STN_INVOICE: 'STN_INVOICE',
  STN_DC: 'STN_DC'
};

const STN_ACTIONS_CONFIG = {
  [STN_ACTION_TYPES.REVERSE_STN]: {
    title: 'Generate Reverse STN',
    confirmMessage: 'Are you sure you want to generate Reverse STN for this Return Order?',
    successMessage: 'Reverse STN generated successfully',
    apiConfig: {
      baseURL: PATH.SI_BASE_URL,
      path: (id) => `/v1/ro/${id}/generate-stn-cn`,
      method: 'POST'
    }
  },
  [STN_ACTION_TYPES.STN_INVOICE]: {
    title: 'Generate STN Document',
    confirmMessage: 'Are you sure you want to generate STN Invoice for this Return Order?',
    successMessage: 'STN Invoice generated successfully',
    apiConfig: {
      baseURL: PATH.SI_BASE_URL,
      path: (id) => `/v1/ro/${id}/generate-stn-invoice`,
      method: 'POST'
    }
  },
  [STN_ACTION_TYPES.STN_DC]: {
    title: 'Generate STN Document',
    confirmMessage: 'Are you sure you want to generate STN DC for this Return Order?',
    successMessage: 'STN DC generated successfully',
    apiConfig: {
      baseURL: PATH.SI_BASE_URL,
      path: (id) => `/v1/ro/${id}/generate-stn-dc`,
      method: 'POST'
    }
  }
};

const DATE_TIME_FORMAT = 'DD MMM YYYY';

const PICKUP_STATUS_META = {
  PICKUP_PENDING: { label: 'Pickup Pending', color: 'gold' },
  READY_FOR_PICKUP: { label: 'Ready for Pickup', color: 'blue' },
  PICKUP_SCHEDULED: { label: 'Pickup Scheduled', color: 'purple' },
  PICKUP_COMPLETE: { label: 'Pickup Complete', color: 'green' }
};

const PICKUP_FROZEN_STATUSES = ['READY_FOR_PICKUP', 'PICKUP_SCHEDULED', 'PICKUP_COMPLETE'];

const getPickupStatusTag = (status) => {
  if (!status) return '-';
  const meta = PICKUP_STATUS_META[status] || { label: status, color: 'default' };
  return (
    <Tag key={status} color={meta.color}>
      {meta.label}
    </Tag>
  );
};

const parseDateValue = (value) => (value ? dayjs(value) : null);
const formatDateValue = (value) => (value ? dayjs(value).format(DATE_TIME_FORMAT) : '-');

const PICKUP_FROZEN_FIELDS = [
  'mail_subject',
  'mail_date',
  'buyer_ro_box_count',
  'buyer_ro_expected_pickup_date',
  'buyer_poc_contact_name',
  'buyer_poc_contact_number',
  'cat_ops_remarks'
];

const PICKUP_DATE_FIELDS = ['mail_date', 'buyer_ro_expected_pickup_date'];
const PICKUP_ALL_DATE_FIELDS = [...PICKUP_DATE_FIELDS, 'buyer_ro_actual_pickup_date'];

const normalizePickupFieldValue = (field, value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  if (PICKUP_DATE_FIELDS.includes(field)) {
    if (dayjs.isDayjs(value)) {
      return value.toISOString();
    }
    return dayjs(value).toISOString();
  }
  if (field === 'buyer_ro_box_count') {
    return Number(value);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

const arePickupFrozenFieldsUnchanged = (formValues, existingValues) => {
  if (!existingValues) return true;
  return PICKUP_FROZEN_FIELDS.every((field) => {
    const formNormalized = normalizePickupFieldValue(field, formValues[field]);
    const existingNormalized = normalizePickupFieldValue(field, existingValues[field]);
    return formNormalized === existingNormalized;
  });
};

const getPickupErrorMessage = (error) => {
  if (error?.response?.data?.status?.statusMessage) {
    return error.response.data.status.statusMessage;
  }
  try {
    const parsed = error?.message ? JSON.parse(error.message) : null;
    if (parsed?.status?.statusMessage) {
      return parsed.status.statusMessage;
    }
  } catch (parseError) {
    // no-op, fallback to default getError output
  }
  return getError(error);
};

const renderPickupDetailsModal = ({
  form,
  showFrozenAlert,
  frozenAlertMessage,
  isBaseFieldsReadOnly,
  showOptionalFields,
  loading,
  onCancel,
  onSubmit,
  open,
  submitLabel,
  readyAction,
  title
}) => {
  const footerContent = (
    // readyAction?.show || readyAction?.completeAction || readyAction?.failedAction ? (
    <Space>
      {readyAction?.completeAction ? (
        <Button
          disabled={readyAction?.completeAction?.disabled}
          loading={readyAction?.completeAction?.loading}
          onClick={readyAction?.completeAction?.onClick}
          type="primary">
          {readyAction?.completeAction?.label || 'Mark Pickup Complete'}
        </Button>
      ) : null}
      {readyAction?.show ? (
        <Button
          disabled={readyAction?.disabled}
          loading={readyAction?.loading}
          onClick={readyAction?.onClick}
          type="primary">
          {readyAction?.label || 'Mark Ready for Pickup'}
        </Button>
      ) : null}
      <Button
        disabled={loading}
        loading={loading}
        onClick={onSubmit}
        type={!readyAction?.show && !readyAction?.completeAction ? 'primary' : undefined}>
        {submitLabel}
      </Button>
      {readyAction?.failedAction ? (
        <Button
          disabled={readyAction?.failedAction?.disabled}
          loading={readyAction?.failedAction?.loading}
          onClick={readyAction?.failedAction?.onClick}>
          {readyAction?.failedAction?.label || 'Pickup Failed'}
        </Button>
      ) : null}
      <Button onClick={onCancel}>Cancel</Button>
    </Space>
    // ) : (
    //   <CommonModalFooter
    //     ifSubmitDisabled={loading}
    //     loading={loading}
    //     onCancel={onCancel}
    //     onSubmit={onSubmit}
    //     submitButtonLabel={submitLabel}
    //   />
  );

  return (
    <ModalWrapper
      content={
        <Form form={form} layout="vertical">
          {showFrozenAlert ? <Alert className="mb-20" message={frozenAlertMessage} showIcon type="info" /> : null}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Mail Subject"
                name="mail_subject"
                rules={[{ required: false, message: 'Please enter mail subject' }]}
                validateFirst>
                <Input disabled={isBaseFieldsReadOnly} placeholder="Enter mail subject" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Mail Date"
                name="mail_date"
                rules={[
                  { required: false, message: 'Please select mail date' },
                  () => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const isFuture = dayjs(value).isAfter(dayjs());
                      if (isFuture) {
                        return Promise.reject(new Error('Mail date cannot be in the future'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
                validateFirst>
                <DatePicker
                  disabled={isBaseFieldsReadOnly}
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                  format={DATE_TIME_FORMAT}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Buyer RO Box Count"
                name="buyer_ro_box_count"
                rules={[{ required: false, message: 'Please enter box count' }]}
                validateFirst>
                <InputNumber disabled={isBaseFieldsReadOnly} min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                dependencies={['mail_date']}
                label="Buyer RO Expected Pick Up Date"
                name="buyer_ro_expected_pickup_date"
                rules={[
                  { required: false, message: 'Please select expected pickup date' }
                  // ({ getFieldValue }) => ({
                  //   validator(_, value) {
                  //     const mailDate = getFieldValue('mail_date');
                  //     if (!value || !mailDate) return Promise.resolve();
                  //     if (value.isBefore(mailDate)) {
                  //       return Promise.reject(new Error('Expected pickup date cannot be before mail date'));
                  //     }
                  //     return Promise.resolve();
                  //   }
                  // })
                ]}
                validateFirst>
                <DatePicker
                  disabled={isBaseFieldsReadOnly}
                  // disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'), 'day')}
                  format={DATE_TIME_FORMAT}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Buyer POC Contact Name"
                name="buyer_poc_contact_name"
                rules={[{ required: false, message: 'Please enter POC name' }]}
                validateFirst>
                <Input disabled={isBaseFieldsReadOnly} placeholder="Enter name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Buyer POC Contact Number"
                name="buyer_poc_contact_number"
                rules={[{ required: false, message: 'Please enter POC number' }]}
                validateFirst>
                <Input disabled={isBaseFieldsReadOnly} placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="CatOps Remarks"
            name="cat_ops_remarks"
            rules={[{ required: false, message: 'Please enter Cat Ops remarks' }]}
            validateFirst>
            <TextArea disabled={isBaseFieldsReadOnly} placeholder="Enter remarks" rows={3} />
          </Form.Item>
          {showOptionalFields ? (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Transporter Pickup ID" name="transporter_pickup_id">
                    <Input placeholder="Enter transporter pickup ID" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    // dependencies={['buyer_ro_expected_pickup_date']}
                    label="Buyer RO Actual Pickup Date"
                    name="buyer_ro_actual_pickup_date"
                    // rules={[
                    //   ({ getFieldValue }) => ({
                    //     validator(_, value) {
                    //       const expectedDate = getFieldValue('buyer_ro_expected_pickup_date');
                    //       if (!value || !expectedDate) return Promise.resolve();
                    //       if (value.isBefore(expectedDate)) {
                    //         return Promise.reject(new Error('Actual pickup date cannot be before expected date'));
                    //       }
                    //       return Promise.resolve();
                    //     }
                    //   })
                    // ]}>
                  >
                    <DatePicker format={DATE_TIME_FORMAT} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Logistics Remarks" name="logistics_remarks">
                <TextArea placeholder="Enter logistics remarks" rows={3} />
              </Form.Item>
            </>
          ) : null}
        </Form>
      }
      footer={footerContent}
      onCancel={onCancel}
      open={open}
      title={title}
    />
  );
};

const getDeliveryResponsibility = (is_transport_handled_by_mensa) => {
  if (is_transport_handled_by_mensa === null) return '-';
  if (is_transport_handled_by_mensa) {
    return 'Mensa';
  }
  return 'Buyer';
};

const getLocationLabel = (type) => {
  const map = {
    OTHER_LOCATION: 'Other Location',
    SHIPPING_LOCATION: 'Shipping Location',
    BILLING_LOCATION: 'Billing Location'
  };

  return map[type] ?? type;
};

const ReturnDetails = ({ deliveredView }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [pod, setPod] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [lostDate, setLostDate] = useState('');
  const [details, setDetails] = useState({});
  const [hasDistanceField, setHasDistanceField] = useState(false);
  const [transporters, setTransporters] = useState([]);
  const [documentCancelRemark, setDocumentCancelRemark] = useState('');
  const [documentCancelReason, setDocumentCancelReason] = useState('');
  const [cancelDocumentType, setCancelDocumentType] = useState('');
  const [cancelOptions, setCancelOptions] = useState({});
  const [pickupForm] = Form.useForm();
  const [dispatchDetails, setDispatchDetails] = useState({
    lr_number: '',
    dispatch_date: '',
    transporter_name: '',
    public_tracking_link: '',
    is_transport_handled_by_mensa: details?.return_order?.is_transport_handled_by_mensa,
    estimated_delivery_date: '',
    lr_file: '',
    vehicle_number: '',
    awb_number: '',
    dispatch_mode: null
  });

  const [loading, setLoading] = useState({
    details: true,
    cancel: false,
    delivery: false,
    ewaybillGenerate: false,
    dispatch: false,
    markAsOpen: false,
    document: false,
    sendForApproval: false,
    approve: false,
    reject: false,
    markAsLost: false,
    generateDc: false,
    transporters: false,
    dispose: false,
    generateCreditNote: false,
    pickupDetailsSubmit: false,
    pickupReady: false,
    pickupComplete: false,
    pickupFailed: false,
    pickupDisposeFlag: false,
    cancelDispose: false
  });

  const [fileLost, setFileLost] = useState({});

  const [dispose, setDispose] = useState({ date: null, remark: null });
  const [cancelDisposeReason, setCancelDisposeReason] = useState('');

  const [creditNoteParams, setCreditNoteParams] = useState({ salesModel: null });
  const [stnActionType, setStnActionType] = useState(null);
  const [stnCancelOption, setStnCancelOption] = useState(null);

  const { attachments, return_order, transitions, actions, credit_note } = details || {};
  const {
    updated_by,
    updated_on,
    status,
    seller_billing_entity,
    seller_billing_address,
    seller_shipping_entity,
    seller_shipping_address,
    buyer_billing_entity,
    buyer_billing_address,
    buyer_shipping_entity,
    buyer_shipping_address,
    currency,
    dispatched_date,
    return_type,
    awb_number,
    order_type,
    total_order_qty,
    total_received_qty,
    total_cancelled_qty,
    ro_number,
    creation_date,
    open_date,
    openDate,
    total_payable_amount,
    document,
    lr_number,
    estimated_delivery_date,
    box_count_delivered,
    transporter_name,
    public_tracking_link,
    is_transport_handled_by_mensa,
    cancellation_reason,
    delivered_date,
    active_delivery_challan,
    active_ewaybill,
    return_so_number,
    return_so_id,
    box_count_dispatched,
    sales_model,
    inward_order_code,
    excess_ro_number,
    fm_actual_date,
    fm_expected_date,
    fm_status,
    fm_remarks,
    lm_expected_date,
    lm_expected_status,
    lm_expected_remarks,
    expected_out_for_delivery_date,
    lm_actual_date,
    lm_actual_status,
    lm_actual_remarks,
    actual_out_for_delivery_date,
    delivery_vehicle_number,
    delivery_transporter_name
  } = return_order || {};
  const isReturnType = return_type === 'RTO';
  const inRoLogisticsScope = isRoLogisticsScope(return_type);
  const activeRoEwayBill = getActiveRoEwayBill(return_order?.return_order_shipping_documents);
  const pickupDetailsData = details?.return_order?.pickup_details;
  const pickupStatus = pickupDetailsData?.pickup_status;
  const isPickupFrozen = PICKUP_FROZEN_STATUSES.includes(pickupStatus);
  const toBeDisposed = details?.return_order?.to_be_disposed;
  // const pickupCanSaveDetails = transitions?.includes('RO_SAVE_PICKUP_DETAILS');
  const pickupCanMarkComplete = ['READY_FOR_PICKUP', 'PICKUP_SCHEDULED'].includes(pickupStatus);
  const canManagePickupDetails = transitions?.includes('RO_SAVE_PICKUP_DETAILS'); // pickupCanSaveDetails || pickupCanMarkComplete;
  const pickupModalTitle = pickupDetailsData ? 'Update Pickup Details' : 'Save Pickup Details';
  const pickupTransitions = {
    ready: transitions?.includes('RO_MARK_READY_FOR_PICKUP'),
    complete: transitions?.includes('RO_MARK_PICKUP_COMPLETE'),
    failed: transitions?.includes('RO_PICKUP_FAILED'),
    dispose: transitions?.includes('RO_MARK_TO_BE_DISPOSED')
  };
  const shouldShowPickupSection = Boolean(details?.return_order?.pickup_details);
  const pickupBaseFieldsReadOnly = isPickupFrozen || pickupCanMarkComplete;
  const pickupShowOptionalFields = pickupCanMarkComplete;
  const pickupModalAlertMessage = pickupCanMarkComplete
    ? 'Pickup completion is in progress. Fields 1-7 are read-only.'
    : 'Fields 1-7 are frozen and cannot be modified after marking Ready for Pickup.';
  const showMarkToBeDisposedButton = pickupTransitions.dispose && !toBeDisposed;
  const showCancelDisposeButton = transitions?.includes('RO_UNMARK_TO_BE_DISPOSED');
  const isToBeDisposed = details?.return_order?.to_be_disposed;
  const headerExtraTags = [];
  if (pickupStatus) {
    headerExtraTags.push(
      <Space key="pickup-status" align="center" size={4}>
        {getPickupStatusTag(pickupStatus)}
      </Space>
    );
  }

  if (isToBeDisposed) {
    headerExtraTags.push(
      <Space key="toBeDisposed" align="center" size={4}>
        <Tag key="toBeDispoased">To Be Disposed</Tag>
      </Space>
    );
  }

  useEffect(() => {
    if (cancelDocumentType) {
      const path = {
        ...apiConfig.return.cancellationOptions,
        path: apiConfig.return.cancellationOptions.path(id)
      };
      const params = { document_type: cancelDocumentType };

      CallApi(
        path,
        params,
        undefined,
        { start: () => setLoading('CANCEL_OPTIONS'), stop: () => setLoading('') },
        (response) => setCancelOptions(response),
        (error) => {
          showErrorMessage(error);
          setOpenModal('');
          setCancelDocumentType('');
        }
      );
    }
  }, [cancelDocumentType, id]);

  useEffect(() => {
    const isDelivered = status === 'DELIVERED';
    if (isDelivered && !deliveredView) {
      navigate(`/sales/return/${id}/delivered`);
    }
  }, [status, id, navigate, deliveredView]);

  useEffect(() => {
    const getTransporters = async () => {
      setLoading((prevState) => ({ ...prevState, transporters: true }));
      try {
        const response = await getMDMTransportClient().transportGet();
        setTransporters(response?.data?.data);
      } catch (error) {
        message.error(getError(error));
        setLoading((prevState) => ({ ...prevState, transporters: false }));
      } finally {
        setLoading((prevState) => ({ ...prevState, transporters: false }));
      }
    };
    if (!transporters?.length && (openModal === 'DISPATCH' || (openModal === 'DELIVERY' && inRoLogisticsScope))) getTransporters();
  }, [openModal, transporters, inRoLogisticsScope]);

  useEffect(() => {
    if (openModal === 'PICKUP_DETAILS') {
      pickupForm.setFieldsValue({
        mail_subject: pickupDetailsData?.mail_subject || '',
        mail_date: parseDateValue(pickupDetailsData?.mail_date),
        buyer_ro_box_count: pickupDetailsData?.buyer_ro_box_count,
        buyer_ro_expected_pickup_date: parseDateValue(pickupDetailsData?.buyer_ro_expected_pickup_date),
        buyer_poc_contact_name: pickupDetailsData?.buyer_poc_contact_name || '',
        buyer_poc_contact_number: pickupDetailsData?.buyer_poc_contact_number || '',
        cat_ops_remarks: pickupDetailsData?.cat_ops_remarks || '',
        transporter_pickup_id: pickupDetailsData?.transporter_pickup_id || '',
        buyer_ro_actual_pickup_date: parseDateValue(pickupDetailsData?.buyer_ro_actual_pickup_date),
        logistics_remarks: pickupDetailsData?.logistics_remarks || ''
      });
    }
  }, [openModal, pickupDetailsData, pickupForm]);

  const fetchDetails = useCallback(() => {
    setLoading((prevState) => ({ ...prevState, details: true }));
    const path = {
      ...apiConfig.return.detail,
      path: apiConfig.return.detail.path(id)
    };

    CallApi(
      path,
      {},
      undefined,
      undefined,
      (response) => {
        const responseDetails = response;
        setDetails(responseDetails);
        setDispatchDetails((prev) => ({
          ...prev,
          is_transport_handled_by_mensa: responseDetails?.return_order?.is_transport_handled_by_mensa
        }));
        if (responseDetails?.transitions?.includes('SO_EWAYBILL_GENERATE_WITH_DISTANCE')) {
          setHasDistanceField(true);
        }
      },
      (error) => message.error(getError(error)),
      () => setLoading((prevState) => ({ ...prevState, details: false }))
    );
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleCancel = () => {
    if (openModal === 'PICKUP_DETAILS') {
      pickupForm.resetFields();
    }
    if (openModal === 'CANCEL_DISPOSE') {
      setCancelDisposeReason('');
    }
    setOpenModal('');
  };

  const handlePOCancel = (reason) => {
    setLoading((prevState) => ({ ...prevState, cancel: true }));
    const cancel = () => {
      const path = {
        ...apiConfig.return.cancel,
        path: apiConfig.return.cancel.path(id)
      };

      const data = { remarks: reason };

      CallApi(
        path,
        undefined,
        data,
        undefined,
        () => {
          navigate(0);
        },
        (error) => message.error(getError(error))
      );
    };
    cancel();
  };

  const onDelivery = (date, file, boxCount, remarks, vehicleNumber, transporterName, dispatchMode) => {
    if (inRoLogisticsScope) {
      const ewbError = validateRoLogisticsAgainstEwayBill({
        transporterName,
        ewayBill: activeRoEwayBill,
        returnType: return_type,
        skipTransporterValidation: isSelfDispatchMode(dispatchMode),
        transporters
      });
      if (ewbError) {
        message.error(ewbError);
        return;
      }
    }

    const delivery = () => {
      const path = {
        ...apiConfig.return.deliver,
        path: apiConfig.return.deliver.path(id)
      };

      const data = {
        pod_date: date,
        pod_file_link: pod,
        event: 'RO_DELIVER',
        boxCount,
        remarks,
        ...(vehicleNumber && { vehicle_number: vehicleNumber }),
        ...(transporterName && { transporter_name: transporterName }),
        ...(dispatchMode && { dispatch_mode: dispatchMode })
      };

      CallApi(
        path,
        undefined,
        data,
        {
          start: () => setLoading((prevState) => ({ ...prevState, delivery: true })),
          stop: () => setLoading((prevState) => ({ ...prevState, delivery: false }))
        },
        () => {
          message.success(`Return Order : ${ro_number} Delivered Successfully`);
          navigate(`/sales/return/${id}/delivered`);
        },
        (error) => {
          message.error(getError(error));
        }
      );
    };
    delivery();
  };

  const handleEWayBillGenerate = (ewayBillDetails) => {
    const {
      eWayBillGenerateAction,
      transportName,
      transportId,
      vehicleNumber,
      vehicleType,
      transportationMode,
      transportDocNumber,
      distance
    } = ewayBillDetails;
    const body = {
      ro_id: id,
      ...(eWayBillGenerateAction && { eway_bill_generate_action: eWayBillGenerateAction }),
      ...(transportName && { transport_name: transportName }),
      ...(transportId && { transport_id: transportId }),
      ...(vehicleNumber && { vehicle_number: vehicleNumber }),
      ...(vehicleType && { vehicle_type: vehicleType }),
      ...(transportationMode && { transportation_mode: transportationMode }),
      ...(transportDocNumber && { transport_doc_number: transportDocNumber }),
      ...(distance && { distance })
    };

    setLoading((prevState) => ({ ...prevState, ewaybillGenerate: true }));
    const path = apiConfig.return.generateEWayBill;

    CallApi(
      path,
      undefined,
      body,
      undefined,
      () => {
        message.success('E-way Bill generated successfully!');
        navigate(0);
      },
      (error) => {
        if (error?.response?.data?.status?.statusCode === 6119) setHasDistanceField(true);
        message.error(getError(error));
        setLoading((prevState) => ({ ...prevState, ewaybillGenerate: false }));
      }
    );
  };

  const onDispatch = () => {
    const {
      lr_number: dispatchLrNumber,
      lr_file: dispatchLrFile,
      public_tracking_link: dispatchTrackingLink,
      estimated_delivery_date: dispatchEstimatedDeliveryDate,
      dispatch_date: dispatchDate,
      transporter_name: dispatchTransporterId,
      is_transport_handled_by_mensa: dispatchIsTransportHandledByMensa,
      box_count: dispatchBoxCount,
      vehicle_number: dispatchVehicleNumber,
      dispatch_mode: dispatchMode
      // awb_number
    } = dispatchDetails;

    const sellerName = seller_billing_entity?.name;
    const isSelf = isSelfDispatchMode(dispatchMode);
    const transporterName = isSelf
      ? sellerName
      : transporters?.find((value) => value?.id === dispatchTransporterId)?.registered_transport_name;

    if (inRoLogisticsScope) {
      const ewbError = validateRoLogisticsAgainstEwayBill({
        transporterName,
        ewayBill: activeRoEwayBill,
        returnType: return_type,
        skipTransporterValidation: isSelf,
        transporters
      });
      if (ewbError) {
        message.error(ewbError);
        return;
      }
    }

    const path = {
      ...apiConfig.return.dispatch,
      path: apiConfig.return.dispatch.path(id)
    };
    const body = {
      dispatch_request_dto: {
        lr_number: dispatchLrNumber,
        lr_file: dispatchLrFile,
        dispatch_date: dispatchDate,
        public_tracking_link: dispatchTrackingLink,
        estimated_delivery_date: dispatchEstimatedDeliveryDate,
        transporter_name: isSelf
          ? sellerName
          : transporters?.find((value) => value?.id === dispatchTransporterId)?.registered_transport_name,
        ...(details?.return_order?.is_transport_handled_by_mensa === null
          ? { is_transport_handled_by_mensa: dispatchIsTransportHandledByMensa }
          : {}),
        // awb_number,
        box_count: dispatchBoxCount,
        transport_id: isSelf ? undefined : dispatchTransporterId,
        vehicle_number: dispatchVehicleNumber,
        dispatch_mode: dispatchMode
      }
    };

    setLoading({ ...loading, dispatch: true });
    CallApi(
      path,
      undefined,
      body,
      undefined,
      () => {
        message.success(`Return Order : ${ro_number} Dispatched Successfully`);
        navigate(0);
      },
      (error) => {
        setLoading({ ...loading, dispatch: false });
        message.error(getError(error));
      }
    );
  };

  const formatPickupDateValue = (value) => {
    if (!value) return undefined;
    if (dayjs.isDayjs(value)) return value.toISOString();
    try {
      return dayjs(value).toISOString();
    } catch (err) {
      return undefined;
    }
  };

  const getPickupPayloadValue = (field, value) => {
    if (pickupBaseFieldsReadOnly && PICKUP_FROZEN_FIELDS.includes(field)) {
      return pickupDetailsData?.[field];
    }
    if (PICKUP_ALL_DATE_FIELDS.includes(field)) {
      return formatPickupDateValue(value);
    }
    if (field === 'buyer_ro_box_count') {
      return value;
    }
    if (typeof value === 'string') {
      return value?.trim() || undefined;
    }
    return value ?? undefined;
  };

  const handlePickupDetailsSubmit = (values) => {
    const path = {
      ...apiConfig.return.pickupDetails,
      path: apiConfig.return.pickupDetails.path(id)
    };

    const body = {
      mail_subject: getPickupPayloadValue('mail_subject', values.mail_subject),
      mail_date: getPickupPayloadValue('mail_date', values.mail_date),
      buyer_ro_box_count: getPickupPayloadValue('buyer_ro_box_count', values.buyer_ro_box_count),
      buyer_ro_expected_pickup_date: getPickupPayloadValue(
        'buyer_ro_expected_pickup_date',
        values.buyer_ro_expected_pickup_date
      ),
      buyer_poc_contact_name: getPickupPayloadValue('buyer_poc_contact_name', values.buyer_poc_contact_name),
      buyer_poc_contact_number: getPickupPayloadValue('buyer_poc_contact_number', values.buyer_poc_contact_number),
      cat_ops_remarks: getPickupPayloadValue('cat_ops_remarks', values.cat_ops_remarks),
      transporter_pickup_id: getPickupPayloadValue('transporter_pickup_id', values.transporter_pickup_id),
      buyer_ro_actual_pickup_date: getPickupPayloadValue(
        'buyer_ro_actual_pickup_date',
        values.buyer_ro_actual_pickup_date
      ),
      logistics_remarks: getPickupPayloadValue('logistics_remarks', values.logistics_remarks)
    };

    CallApi(
      path,
      undefined,
      body,
      {
        start: () => setLoading((prev) => ({ ...prev, pickupDetailsSubmit: true })),
        stop: () => setLoading((prev) => ({ ...prev, pickupDetailsSubmit: false }))
      },
      () => {
        message.success('Pickup details saved successfully');
        pickupForm.resetFields();
        setOpenModal('');
        fetchDetails();
      },
      (error) => message.error(getPickupErrorMessage(error))
    );
  };

  const handleCancelDispose = () => {
    CallApi(
      { ...apiConfig.return.unmarkToBeDisposed, path: apiConfig.return.unmarkToBeDisposed.path(id) },
      { remarks: cancelDisposeReason },
      undefined,
      {
        start: () => setLoading((prev) => ({ ...prev, cancelDispose: true })),
        stop: () => setLoading((prev) => ({ ...prev, cancelDispose: false }))
      },
      () => {
        message.success('Dispose flag removed');
        setCancelDisposeReason('');
        navigate(0);
      },
      (error) => message.error(getPickupErrorMessage(error))
    );
  };

  const invokePickupAction = (endpoint, loadingKey, successMessage) => {
    const path = {
      ...endpoint,
      path: endpoint.path(id)
    };
    CallApi(
      path,
      undefined,
      undefined,
      {
        start: () => setLoading((prev) => ({ ...prev, [loadingKey]: true })),
        stop: () => setLoading((prev) => ({ ...prev, [loadingKey]: false }))
      },
      () => {
        message.success(successMessage);
        navigate(0);
      },
      (error) => message.error(getPickupErrorMessage(error))
    );
  };

  const showPickupConfirm = ({ title, content, onOk, okText = 'Confirm' }) => {
    confirm({
      title,
      icon: <ExclamationCircleFilled />,
      content,
      okText,
      cancelText: 'Cancel',
      className: 'confirm-pop-up',
      onOk
    });
  };

  const handlePickupModalSubmit = () => {
    pickupForm
      .validateFields()
      .then((values) => {
        if (isPickupFrozen && !arePickupFrozenFieldsUnchanged(values, pickupDetailsData)) {
          message.error('Frozen fields (1-7) cannot be modified after marking ready for pickup');
          return;
        }
        handlePickupDetailsSubmit(values);
      })
      .catch(() => {});
  };

  const handleMarkReadyForPickup = () =>
    invokePickupAction(apiConfig.return.markReadyForPickup, 'pickupReady', 'Marked ready for pickup');
  const handleMarkPickupComplete = () =>
    invokePickupAction(apiConfig.return.markPickupComplete, 'pickupComplete', 'Pickup marked as complete');
  const handlePickupFailed = () =>
    invokePickupAction(apiConfig.return.pickupFailed, 'pickupFailed', 'Pickup marked as failed');
  const handleMarkToBeDisposed = () => {
    CallApi(
      { ...apiConfig.return.markToBeDisposed, path: apiConfig.return.markToBeDisposed.path(id) },
      undefined,
      undefined,
      {
        start: () => setLoading((prev) => ({ ...prev, pickupDisposeFlag: true })),
        stop: () => setLoading((prev) => ({ ...prev, pickupDisposeFlag: false }))
      },
      () => {
        message.success('RO marked to be disposed');
        navigate(0);
      },
      (error) => message.error(getPickupErrorMessage(error))
    );
  };

  const handleGenerateCreditNote = () => {
    // setLoading({ ...loading, generateCreditNote: true });
    const path = {
      ...apiConfig.return.generateCreditNote,
      path: apiConfig.return.generateCreditNote.path(id)
    };

    CallApi(
      path,
      creditNoteParams.salesModel ? { salesModel: creditNoteParams.salesModel } : undefined,
      undefined,
      {
        start: () => setLoading({ ...loading, generateCreditNote: true }),
        stop: () => setLoading({ ...loading, generateCreditNote: false })
      },
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        // setLoading({ ...loading, generateCreditNote: false });
      }
    );
  };

  // const generateCreditNote = () => {
  //   confirm({
  //     title: 'Generate Credit Note?',
  //     icon: <ExclamationCircleFilled />,
  //     content: 'Are you sure you want to Generate a Credit Note?',
  //     okType: 'primary',
  //     okText: 'Generate',
  //     className: 'confirm-pop-up',
  //     onOk() {
  //       handleGenerateCreditNote();
  //     },
  //     onCancel() {}
  //   });
  // };

  const generateReverseSTN = () => {
    setStnActionType(STN_ACTION_TYPES.REVERSE_STN);
    setOpenModal('STN_ACTION');
  };

  const generateSTNInvoice = () => {
    setStnActionType(STN_ACTION_TYPES.STN_INVOICE);
    setOpenModal('STN_ACTION');
  };

  const generateSTNDC = () => {
    setStnActionType(STN_ACTION_TYPES.STN_DC);
    setOpenModal('STN_ACTION');
  };

  const handleStnAction = () => {
    const config = STN_ACTIONS_CONFIG[stnActionType];
    if (!config) return;

    const path = {
      ...config.apiConfig,
      path: config.apiConfig.path(id)
    };

    CallApi(
      path,
      undefined,
      undefined,
      {
        start: () => setLoading((prev) => ({ ...prev, stnAction: true })),
        stop: () => setLoading((prev) => ({ ...prev, stnAction: false }))
      },
      (response) => {
        message.success(response?.message || config.successMessage);
        setOpenModal('');
        setStnActionType(null);
        navigate(0);
      },
      (error) => {
        showErrorMessage(error);
        setLoading((prev) => ({ ...prev, stnAction: false }));
      }
    );
  };

  let items = [
    ...(transitions?.includes('RO_GENERATE_STN_DC')
      ? [
          {
            key: 'generate-stn-dc',
            label: (
              <button onClick={generateSTNDC} style={buttonStyle} type="button">
                {STN_ACTIONS_CONFIG.STN_DC.title}
              </button>
            ),
            icon: <FileTextOutlined />
          }
        ]
      : []),
    ...(transitions?.includes('RO_GENERATE_STN_CN')
      ? [
          {
            key: 'generate-reverse-stn',
            label: (
              <button onClick={generateReverseSTN} style={buttonStyle} type="button">
                {STN_ACTIONS_CONFIG.REVERSE_STN.title}
              </button>
            ),
            icon: <FileTextOutlined />
          }
        ]
      : []),
    ...(transitions?.includes('RO_GENERATE_STN_INVOICE')
      ? [
          {
            key: 'generate-stn-invoice',
            label: (
              <button onClick={generateSTNInvoice} style={buttonStyle} type="button">
                {STN_ACTIONS_CONFIG.STN_INVOICE.title}
              </button>
            ),
            icon: <FileTextOutlined />
          }
        ]
      : []),
    ...(transitions?.includes('RO_UPDATE_FM_DATE') || transitions?.includes('RO_UPDATE_LM_DATE')
      ? [
          {
            key: 'add-lm-date',
            label: (
              <button onClick={() => setOpenModal('ADD_LM_DATE')} style={buttonStyle} type="button">
                Add LM Date
              </button>
            ),
            icon: <CalendarOutlined />
          }
        ]
      : []),
    ...(actions?.includes('GENERATE_CREDIT_NOTE')
      ? [
          {
            key: 'generate-credit-note',
            label: (
              <button onClick={() => setOpenModal('GENERATE_CREDIT_NOTE')} style={buttonStyle} type="button">
                Generate Credit Note
              </button>
            ),
            icon: <PlusOutlined />
          }
        ]
      : []),
    ...(actions?.includes('CANCEL_INVOICE')
      ? [
          {
            key: 'cancel-e-invoice',
            label: (
              <button
                onClick={() => {
                  setOpenModal('CANCEL_E_INVOICE');
                  setCancelDocumentType('E_INVOICE');
                }}
                style={buttonStyle}
                type="button">
                Cancel E-Invoice
              </button>
            ),
            icon: <CloseOutlined />
          }
        ]
      : []),
    ...(actions?.includes('CANCEL_DELIVERY_CHALLAN')
      ? [
          {
            key: 'cancel-e-way-bill',
            label: (
              <button
                onClick={() => {
                  setOpenModal('CANCEL_E_INVOICE');
                  setCancelDocumentType('DELIVERY_CHALLAN');
                }}
                style={buttonStyle}
                type="button">
                Cancel Delivery Challan
              </button>
            ),
            icon: <CloseOutlined />
          }
        ]
      : []),
    ...(actions?.includes('CANCEL_CREDIT_NOTE')
      ? [
          {
            key: 'cancel-e-way-bill',
            label: (
              <button
                onClick={() => {
                  setOpenModal('CANCEL_E_INVOICE');
                  setCancelDocumentType('CREDIT_NOTE');
                }}
                style={buttonStyle}
                type="button">
                Cancel Credit Note
              </button>
            ),
            icon: <CloseOutlined />
          }
        ]
      : []),
    ...(actions?.includes('CANCEL_EWAYBILL')
      ? [
          {
            key: 'cancel-e-way-bill',
            label: (
              <button
                onClick={() => {
                  setOpenModal('CANCEL_E_INVOICE');
                  setCancelDocumentType('E_WAY_BILL');
                }}
                style={buttonStyle}
                type="button">
                Cancel E-Way Bill
              </button>
            ),
            icon: <CloseOutlined />
          }
        ]
      : [])
  ];
  if (transitions?.includes('RO_CANCEL')) {
    items = [
      ...items,
      {
        key: 'cancel',
        label: (
          <button onClick={() => setOpenModal('CANCEL')} style={buttonStyle} type="button">
            Cancel RO
          </button>
        ),
        icon: <CloseOutlined />
      }
    ];
  }
  if (transitions?.includes('RO_LOST')) {
    items = [
      ...items,
      {
        key: 'mark-as-lost',
        label: (
          <button onClick={() => setOpenModal('MARK_AS_LOST')} style={buttonStyle} type="button">
            Mark as Lost
          </button>
        ),
        icon: <CloseOutlined />
      }
    ];
  }
  if (showMarkToBeDisposedButton) {
    items = [
      ...items,
      {
        key: 'mark-to-be-disposed',
        label: (
          <button onClick={() => setOpenModal('MARK_TO_BE_DISPOSED')} style={buttonStyle} type="button">
            Dispose
          </button>
        ),
        icon: <DeleteOutlined />
      }
    ];
  }
  if (showCancelDisposeButton) {
    items = [
      ...items,
      {
        key: 'cancel-dispose',
        label: (
          <button onClick={() => setOpenModal('CANCEL_DISPOSE')} style={buttonStyle} type="button">
            Cancel Dispose
          </button>
        ),
        icon: <CloseOutlined />
      }
    ];
  }
  if (transitions?.includes('RO_DISPOSED')) {
    items = [
      ...items,
      {
        key: 'dispose-ro',
        label: (
          <button onClick={() => setOpenModal('DISPOSE')} style={buttonStyle} type="button">
            Dispose RO
          </button>
        ),
        icon: <DeleteOutlined />
      }
    ];
  }
  if (canManagePickupDetails) {
    items = [
      {
        key: 'pickup-details',
        label: (
          <button onClick={() => setOpenModal('PICKUP_DETAILS')} style={buttonStyle} type="button">
            {pickupModalTitle}
          </button>
        ),
        icon: <EditOutlined />
      },
      ...items
    ];
  }
  if (transitions?.includes('RO_LR_UPDATE')) {
    items = [
      ...items,
      {
        key: 'update-lr',
        label: (
          <button onClick={() => setOpenModal('UPDATE_LR')} style={buttonStyle} type="button">
            Update LR
          </button>
        ),
        icon: <EditOutlined />
      }
    ];
  }

  const handleSubmitLmDate = (payload) => {
    const path = {
      ...apiConfig.return.fmLmConnection,
      path: apiConfig.return.fmLmConnection.path(id)
    };

    CallApi(
      path,
      undefined,
      payload,
      {
        start: () => setLoading((prev) => ({ ...prev, fmLmConnection: true })),
        stop: () => setLoading((prev) => ({ ...prev, fmLmConnection: false }))
      },
      () => navigate(0),
      (error) => message.error(getError(error))
    );
  };

  const handleMarkAsOpen = () => {
    setLoading({ ...loading, markAsOpen: true });
    const path = {
      ...apiConfig.return.markAsOpen,
      path: apiConfig.return.markAsOpen.path(id)
    };

    CallApi(
      path,
      undefined,
      undefined,
      undefined,
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, markAsOpen: false });
      }
    );
  };

  const handleDCGenerate = () => {
    setLoading({ ...loading, generateDc: true });
    const path = {
      ...apiConfig.return.generateDeliveryChallan,
      path: apiConfig.return.generateDeliveryChallan.path(id)
    };

    CallApi(
      path,
      undefined,
      undefined,
      undefined,
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, generateDc: false });
      }
    );
  };

  const getHistory = async () => {
    const response = await getHistoryClient().v1CommonEventHistory({
      referenceId: id,
      referenceType: 'SI_RO'
    });
    return response?.data?.data;
  };

  const getAttachmentHistory = async () => {
    const response = await getHistoryClient().v1CommonAttachmentHistory({
      referenceId: id,
      referenceType: 'SI_RO'
    });
    return response?.data?.data;
  };

  const documentsView = (
    <Space className="w-100" direction="vertical">
      <Divider />
      <Title className="mb-30" level={3}>
        Documents
      </Title>
      <Space wrap>
        {attachments?.map((attachment, index) => (
          <DocumentCard
            key={attachment?.id || attachment?.label || attachment?.pre_signed_url || `attachment-${index}`}
            link={attachment?.pre_signed_url}
            name={attachment?.label}
          />
        ))}
      </Space>
    </Space>
  );

  const handleDisposeFileUpload = (file) => {
    const path = apiConfig.return.preSignedUrl;
    const params = { object_key: `return-order/${id}/dispatch/lr/${uuidv4()}.${getFileExtension(file.name)}` };

    CallApi(
      path,
      params,
      undefined,
      {
        start: () => setLoading({ ...loading, document: true }),
        stop: () => setLoading({ ...loading, document: false })
      },
      async (res) => {
        try {
          setLoading((prev) => ({ ...prev, file: true }));
          const response = await axios.put(res, file, {
            headers: {
              'Content-Type': file.type
            }
          });
          if (response.status === 200) {
            // setLoading({ ...loading, document: false });
            setDispose((prev) => ({ ...prev, file: res }));
            setLoading((prev) => ({ ...prev, file: false }));
          }
        } catch {
          message.error('Failed to upload file');
          setLoading((prev) => ({ ...prev, file: false }));
        }
      },
      (error) => {
        message.error(getError(error));
        // setLoading({ ...loading, document: false });
      }
    );
    return false;
  };

  const handleDocumentUpload = (file) => {
    const path = apiConfig.return.preSignedUrl;
    const params = { object_key: `return-order/${id}/dispatch/lr/${uuidv4()}.${getFileExtension(file.name)}` };

    CallApi(
      path,
      params,
      undefined,
      {
        start: () => setLoading({ ...loading, document: true }),
        stop: () => setLoading({ ...loading, document: false })
      },
      async (res) => {
        try {
          setLoading((prev) => ({ ...prev, file: true }));
          const response = await axios.put(res, file, {
            headers: {
              'Content-Type': file.type
            }
          });
          if (response.status === 200) {
            // setLoading({ ...loading, document: false });
            setDispatchDetails((prev) => ({ ...prev, lr_file: res }));
            setLoading((prev) => ({ ...prev, file: false }));
          }
        } catch {
          message.error('Failed to upload file');
          setLoading((prev) => ({ ...prev, file: false }));
        }
      },
      (error) => {
        message.error(getError(error));
        // setLoading({ ...loading, document: false });
      }
    );
    return false;
  };
  // TODO: use common function
  const handlePodUpload = (file) => {
    setLoading({ ...loading, document: true });
    const path = apiConfig.return.preSignedUrl;
    const params = { object_key: `return-order/${id}/delivered/pod/${uuidv4()}.${getFileExtension(file.name)}` };

    CallApi(
      path,
      params,
      undefined,
      undefined,
      () => {},
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, document: false });
      },
      async (res) => {
        try {
          const response = await axios.put(res.data, file, {
            headers: {
              'Content-Type': file.type
            }
          });
          if (response.status === 200) {
            setLoading({ ...loading, document: false });
            setPod(res?.data);
          }
        } catch {
          message.error('Failed to upload file');
        }
      }
    );
    return false;
  };

  const handleDispatchDetailsChange = (key, value) => {
    setDispatchDetails({ ...dispatchDetails, [key]: value });
  };

  const isTransportHandledByMensa = () => {
    // if (dispatchDetails?.is_transport_handled_by_mensa === null) return null;
    return dispatchDetails?.is_transport_handled_by_mensa;
  };

  const isDispatchDisabled = () => {
    // if (isTransportHandledByMensa() === null) return true;
    const {
      dispatch_date: dispatchDateValue,
      estimated_delivery_date: dispatchEstimatedDeliveryDate,
      lr_number: dispatchLrNumber,
      lr_file: dispatchLrFile,
      transporter_name: dispatchTransporterValue,
      box_count: dispatchBoxCount,
      vehicle_number: dispatchVehicleNumber,
      is_transport_handled_by_mensa: dispatchIsTransportHandledByMensa,
      dispatch_mode: dispatchMode
    } = dispatchDetails;
    if (dispatchIsTransportHandledByMensa === null) return true;
    // checks loaders
    if (loading?.dispatch || loading?.file) return true;
    if (inRoLogisticsScope && !dispatchMode) return true;
    const hasTransporter = isSelfDispatchMode(dispatchMode)
      ? Boolean(seller_billing_entity?.name)
      : Boolean(dispatchTransporterValue);
    // checks conditions
    if (isTransportHandledByMensa()) {
      if (!dispatchDateValue) return true;
      if (!dispatchEstimatedDeliveryDate) return true;
      if (!dispatchLrNumber) return true;
      if (!dispatchLrFile) return true;
      if (!hasTransporter) return true;
      if (!dispatchBoxCount) return true;
      if (inRoLogisticsScope && isRoDispatchVehicleMandatory(return_type, dispatchIsTransportHandledByMensa) && !dispatchVehicleNumber?.trim()) {
        return true;
      }
      return false;
    }
    if (!dispatchDateValue) return true;
    if (
      inRoLogisticsScope &&
      isRoDispatchVehicleMandatory(return_type, dispatchIsTransportHandledByMensa) &&
      !dispatchVehicleNumber?.trim()
    ) {
      return true;
    }
    return false;

    // if (transitions?.includes('RO_EDIT_AWB_NUMBER') && !awb_number) return true
  };

  const dispatchDetailsModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical" size="large">
          {is_transport_handled_by_mensa === null ? (
            <Row>
              <Col className="required-after" span={8}>
                Delivery Responsibility
              </Col>
              {/* put radio buttons for Mensa Buyer */}
              <Col span={16}>
                <Select
                  className="w-100"
                  onChange={(value) => handleDispatchDetailsChange('is_transport_handled_by_mensa', value === 'MENSA')}
                  options={[
                    { label: 'Mensa', value: 'MENSA' },
                    ...(isReturnType ? [] : [{ label: 'Buyer', value: 'BUYER' }])
                  ]}
                  value={
                    dispatchDetails?.is_transport_handled_by_mensa === null
                      ? null
                      : dispatchDetails?.is_transport_handled_by_mensa
                        ? 'MENSA'
                        : 'BUYER'
                  }
                />
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col className="required-after" span={8}>
              Dispatch Date
            </Col>
            <Col span={16}>
              <DatePicker
                format={Constants.DATE_FORMAT}
                onChange={(date) => handleDispatchDetailsChange('dispatch_date', date?.toISOString())}
                value={dispatchDetails?.dispatch_date ? dayjs(dispatchDetails?.dispatch_date) : null}
              />
            </Col>
          </Row>
          <Row>
            <Col className={isTransportHandledByMensa() ? 'required-after' : ''} span={8}>
              Estimated Delivery Date
            </Col>
            <Col span={16}>
              <DatePicker
                disabled={!dispatchDetails?.dispatch_date}
                disabledDate={(current) => current.isBefore(getDateFromISO(dispatchDetails?.dispatch_date))}
                format={Constants.DATE_FORMAT}
                onChange={(date) => handleDispatchDetailsChange('estimated_delivery_date', date?.toISOString())}
                value={
                  dispatchDetails?.estimated_delivery_date ? dayjs(dispatchDetails?.estimated_delivery_date) : null
                }
              />
            </Col>
          </Row>
          <Row>
            <Col className={isTransportHandledByMensa() ? 'required-after' : ''} span={8}>
              LR Number
            </Col>
            <Col span={16}>
              <Input
                onChange={(e) => handleDispatchDetailsChange('lr_number', e.target.value?.trim())}
                value={dispatchDetails.lr_number}
              />
            </Col>
          </Row>
          <Row>
            <Col className={isTransportHandledByMensa() ? 'required-after' : ''} span={8}>
              LR File
            </Col>
            <Col span={16}>
              <Upload
                accept="application/pdf, image/jpg, image/jpeg, image/png,text/csv"
                beforeUpload={handleDocumentUpload}
                maxCount={1}
                onRemove={() => setDispatchDetails({ ...dispatchDetails, lr_file: '' })}>
                <Button
                  disabled={!!dispatchDetails?.tracking_file && !!Object.keys(dispatchDetails?.tracking_file).length}
                  loading={loading?.document}
                  size="large">
                  Upload
                </Button>
              </Upload>
            </Col>
          </Row>
          {inRoLogisticsScope ? (
            <>
              <Row>
                <Col className="required-after" span={8}>
                  Dispatch Mode
                </Col>
                <Col span={16}>
                  <Select
                    className="w-100"
                    onChange={(value) => {
                      setDispatchDetails((prev) => ({
                        ...prev,
                        dispatch_mode: value,
                        transporter_name: isSelfDispatchMode(value) ? seller_billing_entity?.name : ''
                      }));
                    }}
                    options={DISPATCH_MODE_OPTIONS}
                    placeholder="Select dispatch mode"
                    value={dispatchDetails?.dispatch_mode}
                  />
                </Col>
              </Row>
              <Row>
                <Col
                  className={
                    isRoDispatchVehicleMandatory(return_type, isTransportHandledByMensa()) ? 'required-after' : ''
                  }
                  span={8}>
                  Vehicle Number
                </Col>
                <Col span={16}>
                  <Input
                    onChange={(e) => handleDispatchDetailsChange('vehicle_number', e.target.value?.trim())}
                    value={dispatchDetails?.vehicle_number}
                  />
                </Col>
              </Row>
            </>
          ) : null}
          <Row>
            <Col className={isTransportHandledByMensa() ? 'required-after' : ''} span={8}>
              Transporter Name
            </Col>
            <Col span={16}>
              {inRoLogisticsScope && isSelfDispatchMode(dispatchDetails?.dispatch_mode) ? (
                <Input disabled value={seller_billing_entity?.name} />
              ) : (
                <Select
                  className="w-100"
                  onChange={(value) => handleDispatchDetailsChange('transporter_name', value)}
                  options={sortTransportersByName(transporters).map((transporter) => ({
                    label: transporter?.registered_transport_name,
                    value: transporter?.id
                  }))}
                  value={dispatchDetails?.transporter_name}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col span={8}>Tracking Link</Col>
            <Col span={16}>
              <Input
                key="trackingLink"
                onChange={(e) => handleDispatchDetailsChange('public_tracking_link', e.target.value?.trim())}
                value={dispatchDetails.public_tracking_link}
              />
            </Col>
          </Row>
          {/* {transitions?.includes('RO_EDIT_AWB_NUMBER') ? (
            <Row>
              <Col className="required-after" span={8}>
                AWB Number
              </Col>
              <Col span={16}>
                <Input key='' onChange={(e) => handleDispatchDetailsChange('awb_number', e.target.value?.trim())} />
              </Col>
            </Row>
          ) : null} */}
          <Row>
            <Col className={isTransportHandledByMensa() ? 'required-after' : ''} span={8}>
              Box Count
            </Col>
            <Col span={16}>
              <InputNumber
                key="boxCount"
                onChange={(e) => handleDispatchDetailsChange('box_count', e)}
                value={dispatchDetails.box_count}
              />
            </Col>
          </Row>
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={isDispatchDisabled()}
          loading={loading?.dispatch}
          onCancel={handleCancel}
          onSubmit={onDispatch}
          submitButtonLabel="Dispatch"
        />
      }
      loading={loading?.transporters}
      onCancel={handleCancel}
      open={openModal === 'DISPATCH'}
      title="Dispatch Details"
    />
  );

  const pickupDetailsModal = renderPickupDetailsModal({
    form: pickupForm,
    showFrozenAlert: pickupBaseFieldsReadOnly,
    frozenAlertMessage: pickupModalAlertMessage,
    isBaseFieldsReadOnly: pickupBaseFieldsReadOnly,
    showOptionalFields: pickupShowOptionalFields,
    loading: loading?.pickupDetailsSubmit,
    onCancel: handleCancel,
    onSubmit: handlePickupModalSubmit,
    open: openModal === 'PICKUP_DETAILS',
    submitLabel: 'Save',
    readyAction: (() => {
      const result = {};

      if (pickupTransitions.ready) {
        result.show = true;
        result.label = 'Mark Ready for Pickup';
        result.loading = loading?.pickupReady;
        result.disabled = loading?.pickupReady;
        result.onClick = () => handleMarkReadyForPickup();
      }

      if (pickupTransitions.complete) {
        result.completeAction = {
          label: 'Mark Pickup Complete',
          loading: loading?.pickupComplete,
          disabled: loading?.pickupComplete,
          onClick: () => handleMarkPickupComplete()
        };
      }

      if (pickupTransitions.failed) {
        result.failedAction = {
          label: 'Pickup Failed',
          loading: loading?.pickupFailed,
          disabled: loading?.pickupFailed,
          onClick: () => handlePickupFailed()
        };
      }

      return Object.keys(result).length > 0 ? result : undefined;
    })(),
    title: 'Pickup Details'
  });

  const banners = {
    open: <Alert className="packed-info-banner" message="Goods are getting ready to be shipped" showIcon type="info" />,
    cancelled: <InfoBanner info={cancellation_reason} label="Sales RO Cancelled" />
  };

  const markRoOpenConfirm = () => {
    confirm({
      title: 'Mark Sales RO as Open?',
      icon: <ExclamationCircleFilled />,
      content: `Once the Return ${ro_number} is marked Open it cannot be edited. Are you sure you want to mark it Open?`,
      okType: 'primary',
      okText: 'Mark as Open',
      className: 'confirm-pop-up',
      onOk() {
        handleMarkAsOpen();
      },
      onCancel() {}
    });
  };

  const handleSendForApproval = async () => {
    setLoading({ ...loading, sendForApproval: true });
    const path = {
      ...apiConfig.return.sendForApproval,
      path: apiConfig.return.sendForApproval.path(id)
    };

    CallApi(
      path,
      undefined,
      undefined,
      undefined,
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, sendForApproval: false });
      }
    );
  };

  const handleApprove = () => {
    setLoading({ ...loading, approve: true });
    const path = {
      ...apiConfig.return.approve,
      path: apiConfig.return.approve.path(id)
    };
    const data = { remarks: approvalReason };

    CallApi(
      path,
      undefined,
      data,
      undefined,
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, approve: false });
      }
    );
  };

  const handleReject = () => {
    setLoading({ ...loading, reject: true });
    const path = {
      ...apiConfig.return.reject,
      path: apiConfig.return.reject.path(id)
    };
    const data = { remarks: rejectionReason };

    CallApi(
      path,
      undefined,
      data,
      undefined,
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, reject: false });
      }
    );
  };

  const handleLost = () => {
    setLoading({ ...loading, markAsLost: true });
    const path = {
      ...apiConfig.return.markAsLost,
      path: apiConfig.return.markAsLost.path(id)
    };
    const data = {
      reference_attachment: fileLost.fileLink,
      remarks: lostReason,
      date: lostDate
    };

    CallApi(
      path,
      undefined,
      data,
      undefined,
      () => navigate(0),
      (error) => {
        message.error(getError(error));
        setLoading({ ...loading, markAsLost: false });
      }
    );
  };

  const handleDispose = () => {
    CallApi(
      { ...apiConfig.return.dispose, path: apiConfig.return.dispose.path(id) },
      undefined,
      {
        date: dispose.date,
        remarks: dispose.remark,
        disposed_proof: dispose?.file
      },
      {
        start: () => setLoading({ ...loading, dispose: true }),
        stop: () => setLoading({ ...loading, dispose: false })
      },
      () => navigate(0),
      (error) => message.error(getError(error))
    );
  };

  const handleLostDocUpload = (file) => {
    const loader = {
      start: setLoading({ ...loading, document: true }),
      stop: setLoading({ ...loading, document: false })
    };

    const objectKey = `return-order/${id}/lost/${uuidv4()}.${getFileExtension(file.name)}`;

    const onSuccess = (fileLink) => setFileLost((prev) => ({ ...prev, fileLink }));

    const onError = (error) => message.error(getError(error));

    try {
      return handleS3Upload(objectKey, file, loader, onSuccess, onError);
    } catch (error) {
      message.error(error.message);
    }

    return false;
  };

  const getSubmitDisabledLost = () => {
    if (lostDate && lostReason && !!Object.keys(fileLost).length) return false;
    return true;
  };

  const getSubmitDisabledDispose = () => {
    if (!dispose.date || dispose.date === '' || !dispose?.remark) return true;
    return false;
  };

  const approvalModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical">
          <Text className="fw-500">Please mention the reason</Text>
          <TextArea
            key="onHold__reason"
            className="mt-10"
            onChange={(e) => setApprovalReason(e.target.value)}
            rows={4}
          />
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={false}
          loading={loading?.approve}
          onCancel={handleCancel}
          onSubmit={handleApprove}
          submitButtonLabel="Approve"
        />
      }
      onCancel={handleCancel}
      open={openModal === 'APPROVE'}
      title="Approve RO"
    />
  );

  const rejectModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical">
          <Text className="required-after fw-500">Please mention the reason</Text>
          <TextArea
            key="onHold__reason"
            className="mt-10"
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
          />
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={!rejectionReason}
          loading={loading?.reject}
          onCancel={handleCancel}
          onSubmit={handleReject}
          submitButtonLabel="Reject"
        />
      }
      onCancel={handleCancel}
      open={openModal === 'REJECT'}
      title="Reject RO"
    />
  );

  const markAsLostModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical">
          <Space className="w-100">
            <Text className="fw-500 required-after">Lost Date</Text>
            <DatePicker format={Constants.DATE_FORMAT} onChange={(date) => setLostDate(date?.toISOString())} />
          </Space>
          <Space className="w-100" direction="vertical">
            <Text className="fw-500 required-after">Please mention the reason</Text>
            <TextArea key="onHold__reason" className="mt-10" onChange={(e) => setLostReason(e.target.value)} rows={4} />
          </Space>
          <Row>
            <Col className="required-after" span={8}>
              Document
            </Col>
            <Col span={16}>
              <Upload
                accept="application/pdf, image/jpg, image/jpeg, image/png, text/csv"
                beforeUpload={handleLostDocUpload}
                maxCount={1}
                onRemove={() => setFileLost({})}>
                <Button disabled={!!Object.keys(fileLost).length} size="large">
                  Upload
                </Button>
              </Upload>
            </Col>
          </Row>
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={getSubmitDisabledLost()}
          loading={loading?.markAsLost}
          onCancel={handleCancel}
          onSubmit={handleLost}
          submitButtonLabel="Mark as Lost"
        />
      }
      onCancel={handleCancel}
      open={openModal === 'MARK_AS_LOST'}
      title="Mark as Lost"
    />
  );

  const markToBeDisposedModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical">
          <Text>Buy marking this you are agree to Return as 'To be Disposed'</Text>
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={loading?.pickupDisposeFlag}
          loading={loading?.pickupDisposeFlag}
          onCancel={handleCancel}
          onSubmit={handleMarkToBeDisposed}
          submitButtonLabel="Mark to be Disposed"
        />
      }
      onCancel={handleCancel}
      open={openModal === 'MARK_TO_BE_DISPOSED'}
      title="Dispose RO"
    />
  );

  const cancelDisposeModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical">
          <Space className="w-100" direction="vertical">
            <Text className="required-after fw-500">Reason for Cancel Dispose</Text>
            <Input
              onChange={(e) => setCancelDisposeReason(e.target.value)}
              placeholder="Enter reason"
              value={cancelDisposeReason}
            />
          </Space>
        </Space>
      }
      footer={
        <CommonModalFooter
          cancelButtonLabel="Cancel"
          ifSubmitDisabled={!cancelDisposeReason?.trim()}
          loading={loading?.cancelDispose}
          onCancel={handleCancel}
          onSubmit={handleCancelDispose}
          submitButtonLabel="Cancel Dispose"
        />
      }
      onCancel={handleCancel}
      open={openModal === 'CANCEL_DISPOSE'}
      title="Dispose RO"
    />
  );

  const disposeModal = (
    <ModalWrapper
      content={
        <Space className="w-100" direction="vertical">
          <Space className="w-100">
            <Text className="fw-500 required-after">Dispose Date</Text>
            <DatePicker
              format={Constants.DATE_FORMAT}
              onChange={(date) => setDispose((prev) => ({ ...prev, date: date?.toISOString() }))}
            />
          </Space>
          <Space>
            <Row>
              <Col span={8}>File</Col>
              <Col span={16}>
                <Upload
                  accept="application/pdf, image/jpg, image/jpeg, image/png,text/csv"
                  beforeUpload={handleDisposeFileUpload}
                  maxCount={1}
                  onRemove={() => setDispose({ ...dispose, file: '' })}>
                  <Button
                    disabled={dispose?.file ? !!Object.keys(dispose?.file).length : null}
                    loading={loading?.document}
                    size="large">
                    Upload
                  </Button>
                </Upload>
              </Col>
            </Row>
          </Space>
          <Space className="w-100" direction="vertical">
            <Text className="fw-500 required-after">Please mention the reason</Text>
            <TextArea
              key="dispose__reason"
              className="mt-10"
              onChange={(e) => setDispose((prev) => ({ ...prev, remark: e.target.value }))}
              rows={4}
            />
          </Space>
        </Space>
      }
      footer={
        <CommonModalFooter
          ifSubmitDisabled={getSubmitDisabledDispose()}
          loading={loading?.dispose}
          onCancel={handleCancel}
          onSubmit={handleDispose}
          submitButtonLabel="Dispose"
        />
      }
      onCancel={handleCancel}
      open={openModal === 'DISPOSE'}
      title="Dispose RO"
    />
  );

  const actionButtons = (
    <Space className="btns-container" wrap>
      {!!actions?.includes('GENERATE_DELIVERY_CHALLAN') && (
        <Space size="large">
          <Button
            disabled={loading?.generateDc}
            loading={loading?.generateDc}
            onClick={handleDCGenerate}
            type="primary">
            Generate DC
          </Button>
        </Space>
      )}
      {!!actions?.includes('GENERATE_EWAYBILL') && (
        <Space size="large">
          <Button onClick={() => setOpenModal('EWAYBILL_GENERATE')} type="primary">
            Generate E-Way Bill
          </Button>
        </Space>
      )}
      {!!transitions?.includes('RO_SEND_FOR_APPROVAL') && (
        <Button
          disabled={loading?.sendForApproval}
          loading={loading?.sendForApproval}
          onClick={handleSendForApproval}
          type="primary">
          Send for Approval
        </Button>
      )}
      {!!transitions?.includes('RO_OPEN') && (
        <Button disabled={loading?.markAsOpen} loading={loading?.markAsOpen} onClick={markRoOpenConfirm} type="primary">
          Mark as Open
        </Button>
      )}
      {!!transitions?.includes('RO_APPROVE') && (
        <Button
          disabled={loading?.approve}
          loading={loading?.approve}
          onClick={() => setOpenModal('APPROVE')}
          type="primary">
          Approve
        </Button>
      )}
      {!!transitions?.includes('RO_REJECT') && (
        <Button danger disabled={loading?.reject} loading={loading?.reject} onClick={() => setOpenModal('REJECT')}>
          Reject
        </Button>
      )}
      {!!transitions?.includes('SO_DELIVERY_CHALLAN_GENERATE') && (
        <Button onClick={() => setOpenModal('DELIVERY_CHALLAN')} type="primary">
          Generate Delivery Challan
        </Button>
      )}
      {!!transitions?.includes('RO_DISPATCH') && (
        <Button onClick={() => setOpenModal('DISPATCH')} type="primary">
          Mark as Dispatched
        </Button>
      )}
      {!!transitions?.includes('RO_DELIVER') && (
        <Button onClick={() => setOpenModal('DELIVERY')} type="primary">
          Mark as Delivered
        </Button>
      )}
      {/* {pickupTransitions.ready ? (
        <Button
          disabled={loading?.pickupReady}
          loading={loading?.pickupReady}
          onClick={() =>
            showPickupConfirm({
              title: 'Mark Ready for Pickup?',
              content: 'Are you sure you want to mark this Return Order ready for pickup?',
              onOk: handleMarkReadyForPickup
            })
          }
          type="primary">
          Mark Ready for Pickup
        </Button>
      ) : null} */}
    </Space>
  );

  const viewProps = {
    items: [
      {
        title: '',
        key: '',
        cols: [
          {
            title: '',
            items: [
              { label: 'RO Type', value: order_type },
              ...(return_order?.rto_ship_to_location
                ? [{ label: 'Return Location', value: getLocationLabel(return_order?.rto_ship_to_location) }]
                : []),

              ...(return_so_id
                ? [
                    {
                      label: 'Return SO Number',
                      value: <Link to={`/sales/domestic-sales-order/${return_so_id}`}>{return_so_number}</Link>
                    }
                  ]
                : []),
              { label: 'Return Type', value: return_type },
              ...(excess_ro_number ? [{ label: 'Excess RO Number', value: excess_ro_number }] : []),
              ...(!isReturnType ? [{ label: 'AWB Number', value: awb_number }] : [])
            ]
          },
          {
            title: '',
            items: [
              {
                label: 'Creation Date',
                value: creation_date ? dayjs(creation_date).format(Constants.DATE_FORMAT) : '-'
              },
              {
                label: 'Open Date',
                value: open_date || openDate ? dayjs(open_date || openDate).format(Constants.DATE_FORMAT) : '-'
              },
              {
                label: 'Delivery Responsibility',
                value: getDeliveryResponsibility(is_transport_handled_by_mensa)
              },
              ...(inward_order_code ? [{ label: 'Inward Order Code', value: inward_order_code }] : [])
            ]
          }
        ]
      },
      ...(shouldShowPickupSection
        ? [
            {
              title: 'Pickup Details',
              key: 'pickup-details',
              cols: [
                {
                  title: '',
                  items: [
                    {
                      label: 'Pickup Status',
                      value: (
                        <Space>
                          {getPickupStatusTag(pickupStatus)}
                          {toBeDisposed ? <Tag color="red">To Be Disposed</Tag> : null}
                        </Space>
                      )
                    },
                    { label: 'Mail Subject', value: pickupDetailsData?.mail_subject || '-' },
                    { label: 'Mail Date', value: formatDateValue(pickupDetailsData?.mail_date) },
                    { label: 'Buyer RO Box Count', value: pickupDetailsData?.buyer_ro_box_count ?? '-' },
                    {
                      label: 'Buyer RO Expected Pick Up Date ',
                      value: formatDateValue(pickupDetailsData?.buyer_ro_expected_pickup_date)
                    },
                    {
                      label: 'Buyer POC Contact Name',
                      value: pickupDetailsData?.buyer_poc_contact_name || '-'
                    },
                    {
                      label: 'Buyer POC Contact Number',
                      value: pickupDetailsData?.buyer_poc_contact_number || '-'
                    },
                    { label: 'CatOps Remarks', value: pickupDetailsData?.cat_ops_remarks || '-' }
                  ]
                },
                {
                  title: '',
                  items: [
                    {
                      label: 'Transporter Pickup ID',
                      value: pickupDetailsData?.transporter_pickup_id || '-'
                    },
                    {
                      label: 'Buyer RO Actual Pickup Date',
                      value: formatDateValue(pickupDetailsData?.buyer_ro_actual_pickup_date)
                    },
                    { label: 'Logistics Remarks', value: pickupDetailsData?.logistics_remarks || '-' }
                  ]
                }
              ]
            }
          ]
        : []),
      {
        title: 'Ordered Qty Details',
        key: '',
        cols: [
          {
            title: '',
            items: [
              { label: 'Ordered Qty', value: total_order_qty },
              { label: 'Received Qty', value: total_received_qty },
              { label: 'Cancelled Qty', value: total_cancelled_qty }
            ]
          },
          {
            title: '',
            items: [
              {
                label: 'Ordered Amt(inc. taxes)',
                value: getFormattedAmount(currency, total_payable_amount)
              }
            ]
          }
        ]
      },
      {
        title: '',
        key: '',
        cols: [
          {
            title: 'Seller Billing Details',
            key: '',
            items: [
              {
                label: 'Name',
                value: seller_billing_entity?.name
              },
              {
                label: 'Address',
                value: <Address entity={seller_billing_address} />
              }
            ]
          },
          {
            title: 'Seller Shipping Details',
            key: '',
            items: [
              {
                label: 'Name',
                value: seller_shipping_entity?.name
              },
              {
                label: 'Address',
                value: <Address entity={seller_shipping_address} />
              }
            ]
          }
        ]
      },
      {
        title: '',
        key: '',
        cols: [
          {
            title: 'Buyer Billing Details',
            key: '',
            items: [
              {
                label: 'Name',
                value: buyer_billing_entity?.name
              },
              {
                label: 'Address',
                value: <Address entity={buyer_billing_address} />
              }
            ]
          },
          {
            title: 'Buyer Shipping Details',
            key: '',
            items: [
              {
                label: 'Name',
                value: buyer_shipping_entity?.name
              },
              {
                label: 'Address',
                value: <Address entity={buyer_shipping_address} />
              }
            ]
          }
        ]
      },
      ...(status === 'DISPATCHED' || status === 'DELIVERED' || status === 'LOST'
        ? [
            {
              title: 'Dispatch Details',
              key: '',
              cols: [
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'LR Number',
                      value: getLatestLrNumber(lr_number)
                    },
                    {
                      label: 'Dispatch Date',
                      value: getDateFromISO(dispatched_date)
                    },
                    {
                      label: 'Estimated Delivery Date',
                      value: getDateFromISO(estimated_delivery_date)
                    },
                    ...(box_count_dispatched ? [{ label: 'Box Count Dispatched', value: box_count_dispatched }] : [])
                  ]
                },
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'Transporter Name',
                      value: transporter_name
                    },
                    {
                      label: 'Tracking Link',
                      value: public_tracking_link ? (
                        <Link
                          className="accent-color"
                          href={
                            public_tracking_link.includes('http')
                              ? public_tracking_link
                              : `https://${public_tracking_link}`
                          }
                          target="_blank">
                          {public_tracking_link}
                        </Link>
                      ) : (
                        '-'
                      )
                    },
                    {
                      label: 'Delivery Responsibility',
                      value: getDeliveryResponsibility(is_transport_handled_by_mensa)
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      ...(delivered_date
        ? [
            {
              title: 'Delivery Details',
              key: '',
              cols: [
                {
                  title: '',
                  items: [
                    {
                      label: 'Delivery Date',
                      value: getDateFromISO(delivered_date)
                    },
                    ...(box_count_delivered ? [{ label: 'Box Count Delivered', value: box_count_delivered }] : []),
                    ...(delivery_vehicle_number
                      ? [{ label: 'Vehicle Number', value: delivery_vehicle_number }]
                      : []),
                    ...(delivery_transporter_name
                      ? [{ label: 'Transporter Name', value: delivery_transporter_name }]
                      : [])
                  ]
                }
              ]
            }
          ]
        : []),
      ...(!isReturnType
        ? [
            {
              title: 'Documents Details',
              key: '',
              cols: [
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'Document Number',
                      value: document?.document_number
                    },
                    {
                      label: 'Document Type',
                      value: document?.document_type
                    },
                    {
                      label: 'DC Qty.',
                      value: document?.document_qty
                    },
                    {
                      label: 'DC Value',
                      value: getFormattedAmount(currency, document?.document_amount)
                    },
                    {
                      label: 'Box Count',
                      value: document?.box_count
                    }
                  ]
                },
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'Document Date',
                      value: getDateFromISO(document?.document_date)
                    },
                    {
                      label: 'Reference Number',
                      value: document?.reference_number
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      ...(credit_note?.cn_number
        ? [
            {
              title: 'Credit Note Details',
              key: '',
              cols: [
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'CN No.',
                      value: credit_note?.cn_number
                    },
                    {
                      label: 'CN Qty',
                      value: credit_note?.total_order_qty
                    }
                  ]
                },
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'CN Date',
                      value: getDateFromISO(credit_note?.created_on)
                    },
                    {
                      label: 'CN Value',
                      value: getFormattedAmount(credit_note?.currency, credit_note?.total_payable_amount)
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      ...(active_delivery_challan?.document_number
        ? [
            {
              title: 'Delivery Challan Details',
              key: '',
              cols: [
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'DC No.',
                      value: active_delivery_challan?.document_number
                    }
                  ]
                },
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'DC Date',
                      value: getDateFromISO(active_delivery_challan?.issue_date)
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      ...(fm_actual_date || fm_expected_date
        ? [
            {
              title: 'FM Date',
              key: '',
              cols: [
                {
                  title: '',
                  key: '',
                  items: [
                    {
                      label: 'FM Actual Date',
                      value: getDateFromISO(fm_actual_date)
                    },
                    {
                      label: 'FM Expected Date',
                      value: getDateFromISO(fm_expected_date)
                    },
                    {
                      label: 'FM Status',
                      value: fm_status
                    },
                    {
                      label: 'FM Remarks',
                      value: fm_remarks
                    }
                  ]
                }
              ]
            }
          ]
        : []),
      ...(lm_expected_date ||
      lm_actual_date ||
      expected_out_for_delivery_date ||
      actual_out_for_delivery_date ||
      lm_expected_status ||
      lm_actual_status ||
      lm_expected_remarks ||
      lm_actual_remarks
        ? [
            {
              title: 'LM Date',
              key: '',
              cols: [
                ...(lm_expected_date || expected_out_for_delivery_date || lm_expected_status || lm_expected_remarks
                  ? [
                      {
                        title: 'LM Arrival Date Expected',
                        key: '',
                        items: [
                          {
                            label: 'LM Expected Date',
                            value: getDateFromISO(lm_expected_date)
                          },
                          {
                            label: 'LM Status',
                            value: lm_expected_status
                          },
                          {
                            label: 'Out for Delivery Date',
                            value: getDateFromISO(expected_out_for_delivery_date)
                          },
                          {
                            label: 'Remarks',
                            value: lm_expected_remarks
                          }
                        ]
                      }
                    ]
                  : []),
                ...(lm_actual_date || actual_out_for_delivery_date || lm_actual_status || lm_actual_remarks
                  ? [
                      {
                        title: 'LM Arrival Date Actual',
                        key: '',
                        items: [
                          {
                            label: 'LM Actual Date',
                            value: getDateFromISO(lm_actual_date)
                          },
                          {
                            label: 'LM Actual Status',
                            value: lm_actual_status
                          },
                          {
                            label: 'Out for Delivery Date',
                            value: getDateFromISO(actual_out_for_delivery_date)
                          },
                          {
                            label: 'Remarks',
                            value: lm_actual_remarks
                          }
                        ]
                      }
                    ]
                  : [])
              ]
            }
          ]
        : [])
    ]
  };

  if (deliveredView) {
    return <CustomView {...viewProps} />;
  }

  const handleInvoiceCancel = () => {
    const paths = {
      E_INVOICE: apiConfig.return.cancelInvoice,
      E_WAY_BILL: apiConfig.return.cancelEWayBill,
      DELIVERY_CHALLAN: apiConfig.return.cancelDeliveryChallan,
      CREDIT_NOTE: apiConfig.return.cancelCreditNote
    };
    const path = {
      ...paths[cancelDocumentType],
      path: paths[cancelDocumentType].path(id)
    };
    const params = { remark: documentCancelRemark, reason: documentCancelReason };
    if (stnCancelOption === 'both') {
      params.cancel_stn_document = true;
    }

    CallApi(
      path,
      params,
      undefined,
      { start: () => setLoading('DOCUMENT_CANCEL'), stop: () => setLoading('') },
      (response) => navigate(0),
      showErrorMessage
    );
  };

  const typeOptionMap = {
    PLATFORM_REJECTION: ['OR', 'SOR'],
    STOCK_CORRECTION: ['OR', 'SOR'],
    QC_PLATFORM_REJECTION: ['OR', 'SOR'],
    QC_STOCK_CORRECTION: ['OR', 'SOR'],
    QC_EXCESS: null,
    EXCESS: null,
    OTHER: ['OR', 'SOR'],
    B2C_RETURN: null,
    REFURBISH_INTERNAL: null,
    REFURBISH_EXTERNAL: null,
    BUYER_STOCK_TRANSFER: null,
    DF: ['DF']
  };

  const validateCreditNoteParams = (type, salesModel) => {
    if (salesModel) return true;
    const options = typeOptionMap[type] || [];
    const isSalesModelRequired = options.length > 0;
    const isSalesModelSelected = creditNoteParams?.salesModel && options.includes(creditNoteParams?.salesModel);
    if (isSalesModelRequired && !isSalesModelSelected) {
      return false;
    }
    return true;
  };

  const getCreditNoteContent = (type, salesModel) => {
    if (salesModel) {
      return (
        <div>
          <ExclamationCircleTwoTone twoToneColor="#faad14" />
          <Text style={{ marginLeft: 10 }}>Are you sure you want to Generate a Credit Note?</Text>
        </div>
      );
    }
    const options = typeOptionMap[type];
    if (!options) {
      return (
        <div>
          <ExclamationCircleTwoTone twoToneColor="#faad14" />
          <Text style={{ marginLeft: 10 }}>Are you sure you want to Generate a Credit Note?</Text>
        </div>
      );
    }
    return (
      <Space className="w-100" direction="vertical">
        <Text className="fw-500 required-after">Please select Sales Model</Text>
        <Select
          className="w-100"
          onChange={(value) => setCreditNoteParams((prev) => ({ ...prev, salesModel: value }))}
          options={options?.map((option) => ({
            label: option,
            value: option
          }))}
        />
      </Space>
    );
  };

  const generateCreditNoteModal = (
    <ModalWrapper
      content={getCreditNoteContent(return_type, sales_model)}
      footer={
        <CommonModalFooter
          // dangerSubmitButton
          ifSubmitDisabled={loading.generateCreditNote || !validateCreditNoteParams(return_type, sales_model)}
          isCancelDisabled={loading.generateCreditNote}
          loading={loading.generateCreditNote}
          onCancel={() => {
            if (!loading.generateCreditNote) setOpenModal('');
          }}
          onSubmit={handleGenerateCreditNote}
          submitButtonLabel="Generate Credit Note"
        />
      }
      onCancel={() => {
        setOpenModal('');
      }}
      open={openModal === 'GENERATE_CREDIT_NOTE'}
      title="Generate Credit Note"
      // loading={loading === 'CANCEL_OPTIONS'}
    />
  );

  const cancelEInvoiceModal = (
    <ModalWrapper
      content={
        <Space direction="vertical w-100">
          <Text>{cancelOptions?.message}</Text>
          <Space className="w-100" direction="vertical">
            {cancelOptions?.stn_document_number ? (
              <Radio.Group onChange={(e) => setStnCancelOption(e.target.value)} value={stnCancelOption}>
                <Radio value="single">Single Document (Channel CN)</Radio>
                <Radio value="both">Both Documents (Channel CN & {cancelOptions?.stn_document_type})</Radio>
              </Radio.Group>
            ) : null}
            <Text className="required-after fw-500">Reason</Text>
            <Select
              className="w-100"
              onChange={(value) => setDocumentCancelReason(value)}
              options={Constants.DOCUMENT_CANCEL_REASONS}
            />
          </Space>
          <Text className="required-after fw-500">{cancelOptions?.reason_placeholder}</Text>
          <TextArea className="mt-10" onChange={(e) => setDocumentCancelRemark(e.target.value.trim())} rows={4} />
        </Space>
      }
      footer={
        <CommonModalFooter
          cancelButtonLabel={cancelOptions?.cancel_button_label}
          dangerSubmitButton
          ifSubmitDisabled={
            loading === 'CANCEL_E_INVOICE' ||
            !documentCancelRemark ||
            !documentCancelReason ||
            (cancelOptions?.stn_document_number && !stnCancelOption)
          }
          isCancelDisabled={loading === 'DOCUMENT_CANCEL'}
          loading={loading === 'DOCUMENT_CANCEL'}
          onCancel={() => {
            if (loading !== 'DOCUMENT_CANCEL') {
              setOpenModal('');
              setStnCancelOption(null);
            }
          }}
          onSubmit={handleInvoiceCancel}
          submitButtonLabel={cancelOptions?.confirm_button_label}
        />
      }
      loading={loading === 'CANCEL_OPTIONS'}
      onCancel={() => {
        if (loading !== 'DOCUMENT_CANCEL') {
          setOpenModal('');
          setStnCancelOption(null);
        }
      }}
      open={openModal === 'CANCEL_E_INVOICE'}
      title={cancelOptions?.title}
    />
  );

  const stnActionModal = (
    <ModalWrapper
      content={
        <div>
          <ExclamationCircleTwoTone twoToneColor="#faad14" />
          <Text style={{ marginLeft: 10 }}>
            {stnActionType ? STN_ACTIONS_CONFIG[stnActionType]?.confirmMessage : null}
          </Text>
        </div>
      }
      footer={
        <CommonModalFooter
          dangerSubmitButton
          ifSubmitDisabled={loading?.stnAction}
          loading={loading?.stnAction}
          onCancel={() => {
            setOpenModal('');
            setStnActionType(null);
          }}
          onSubmit={handleStnAction}
          submitButtonLabel="Confirm"
        />
      }
      onCancel={() => {
        setOpenModal('');
        setStnActionType(null);
      }}
      open={openModal === 'STN_ACTION'}
      title={stnActionType ? STN_ACTIONS_CONFIG[stnActionType]?.title : null}
    />
  );

  return (
    <AppLayout selected="returnOrder">
      {!!loading?.details && <Skeleton />}
      {!loading?.details && (
        <Space direction="vertical">
          <LmDateModal
            initialData={return_order}
            loading={loading?.addLmDate}
            onClose={() => setOpenModal('')}
            onSubmit={handleSubmitLmDate}
            open={openModal === 'ADD_LM_DATE'}
            transitions={transitions}
          />
          {pickupDetailsModal}
          {dispatchDetailsModal}
          {approvalModal}
          {rejectModal}
          {markAsLostModal}
          {markToBeDisposedModal}
          {cancelDisposeModal}
          {disposeModal}
          {cancelEInvoiceModal}
          {generateCreditNoteModal}
          {stnActionModal}
          <CancelModal
            isOpen={openModal === 'CANCEL'}
            loading={loading?.cancel}
            onCancel={handleCancel}
            onOk={handlePOCancel}
            type="RO"
          />
          <EWayBillDetailsModal
            hasDistanceField={hasDistanceField}
            isOpen={openModal === 'EWAYBILL_GENERATE'}
            loading={loading?.ewaybillGenerate}
            onCancel={handleCancel}
            onOk={handleEWayBillGenerate}
          />
          <DeliveryDetailsModal
            dispatchDate={getDateFromISO(dispatched_date)}
            isOpen={openModal === 'DELIVERY'}
            loading={loading?.delivery}
            logisticsMandatory={isRoDeliveryLogisticsMandatory(return_type)}
            onCancel={handleCancel}
            onFileUpload={handlePodUpload}
            onOk={onDelivery}
            lockToSelf={wasDispatchedAsSelf(transporter_name, seller_billing_entity?.name)}
            sellerName={seller_billing_entity?.name}
            showLogisticsFields={inRoLogisticsScope}
          />
          <UpdateLrModal
            apiEndpoint={apiConfig.return.patchLr}
            apiParams={{ roId: id }}
            defaultLrDate={dispatched_date}
            defaultLrNumber={lr_number || ''}
            deliveryDate={delivered_date}
            eWayBillDate={active_ewaybill?.issue_date}
            isOpen={openModal === 'UPDATE_LR'}
            onCancel={handleCancel}
            onSuccess={() => navigate(0)}
          />
          <CustomHeader
            attachmentHistoryFetchFn={getAttachmentHistory}
            // extraTags={headerExtraTags}
            history={[]}
            historyFetchFn={getHistory}
            info={{
              updatedBy: updated_by,
              updatedOn: dayjs(updated_on).format(Constants.DATE_FORMAT)
            }}
            kebabMenu={
              <Dropdown menu={{ items }}>
                <div className="menu" />
              </Dropdown>
            }
            tag={status}
            title={`Sales RO : ${ro_number}`}
          />
          <Divider />
          {status === 'CANCELLED' && banners.cancelled}
          <CustomView {...viewProps} />
          {!!attachments?.length && documentsView}
          {actionButtons}
        </Space>
      )}
    </AppLayout>
  );
};

export default ReturnDetails;
